import GuestProfileForm from "../guest-profile-form/guest-profile-form";
import { useApi, useModal, useMount, useRouter } from "hooks";
import React, { useCallback, useMemo } from "react";
import lang from "translations";
import { editGuestProfile, getGuestProfile, deleteGuestProfile } from "apis/guest-profile.api";
import { editGuestProfileRequest, guestProfileIdResponse } from "mappers";
import { DeleteModal } from "components/modals";
import { Text, Toast } from "components/commons";
import { Path } from "paths";
import { mixpanel, TrackEvent } from "mixpanel";
import { updateEatAppRoomsMapping } from "apis/eat-app.api";

const EditGuestProfile = () => {
  const { query, history } = useRouter();
  const { id } = query;

  const deleteModal = useModal();

  const {
    request,
    loading = true,
    error,
    mappedData,
  } = useApi({
    api: getGuestProfile,
    mapper: guestProfileIdResponse,
    params: {
      guestProfileId: id,
    },
    handleOwnError: true,
  });

  const { name, guestProfileDefault } = mappedData || {};

  const { request: submit, loading: submitting } = useApi({
    api: editGuestProfile,
    handleOwnError: {
      badrequest: true,
    },
    paramsMapper: editGuestProfileRequest,
    params: {
      guestProfileId: id,
    },
  });

  const { request: deleteRequest, loading: deleting } = useApi({
    api: deleteGuestProfile,
    params: {
      guestProfileId: id,
    },
  });

  const { request: updateRoomsMapping, loading: mapping } = useApi({
    api: updateEatAppRoomsMapping,
    handleOwnError: true,
  });

  const submitForm = useCallback(
    async (params) => {
      try {
        const res = await submit(params);

        if (params.eatAppRoomId) {
          await updateRoomsMapping({
            venueId: params.venueId,
            roomsMapping: [
              {
                eatAppId: params.eatAppRoomId,
                internalGuestProfileId: Number(id),
              },
            ],
          });
        }

        return {
          response: res,
          message: lang.changesSaved,
        };
      } catch (e) {
        throw e;
      }
    },
    [id, submit, updateRoomsMapping]
  );

  useMount(() => {
    request();
    mixpanel.track(TrackEvent.VisitedPage, {
      Page: lang.editGuestProfile,
    });
  });

  const initialState = useMemo(() => {
    const {
      name,
      description,
      questions,
      vouchers,
      isAskQuestion,
      customQuestionIncludes,
      customQuestionsRequired,
      sortQuestions,
      isAllowedPostLimit,
      limit,
      freeCredit,
      amount,
      defaultQuestionIncludes,
      isTagRegistration,
      eatAppRoomId,
      profileDiscount,
    } = mappedData || {};
    return {
      name,
      description,
      questions,
      vouchers,
      isAskQuestion,
      customQuestionIncludes,
      customQuestionsRequired,
      sortQuestions,
      isAllowedPostLimit,
      limit,
      freeCredit,
      amount,
      defaultQuestionIncludes,
      isTagRegistration,
      eatAppRoomId,
      profileDiscount,
    };
  }, [mappedData]);

  return (
    <>
      <GuestProfileForm
        error={error}
        loading={loading}
        title={name}
        submit={submitForm}
        submitting={submitting || deleting || mapping}
        initialState={initialState}
        deleteButton={
          guestProfileDefault
            ? null
            : {
                onClick: () => {
                  deleteModal.show();
                },
                text: lang.deleteGuestProfile,
              }
        }
      />
      <DeleteModal
        {...deleteModal}
        deleting={deleting}
        primaryText={lang.deleteGuestProfile}
        ok={async () => {
          await deleteRequest();
          Toast({
            content: lang.guestProfileDeleted,
            success: true,
            icon: "check",
          }).open();
          history.push(Path.GUEST_PROFILE);
        }}
        customContent={
          <div>
            <Text>{lang.youAreAboutToDeleteGuestProfile}</Text>
          </div>
        }
        customHeader={
          <Text modalTitle className="py-md">
            {lang.askDeleteGuestProfile}
          </Text>
        }
      />
    </>
  );
};

export default EditGuestProfile;
